import { Alert, Button, Group, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconInfoCircle } from '@tabler/icons-react';
import { createId } from '@paralleldrive/cuid2';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useAtomValue, useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import { clearSelectionsAtom, selectedOpsIdsAtom, selectedStepsIdsAtom } from '~/atoms/no-code-atoms';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';

export function CreateComponentConfirmationDialog({
  selectedItemsCount,
  selectedItemType,
  testContentId,
  onCancel,
}: {
  selectedItemsCount: number;
  selectedItemType: 'operations' | 'steps' | 'mixed';
  testContentId: string;
  onCancel: () => void;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const projectId = useProjectId();
  const trpcUtils = api.useUtils();

  // Get selection state from atoms
  const selectedStepsIds = useAtomValue(selectedStepsIdsAtom);
  const selectedOpsIds = useAtomValue(selectedOpsIdsAtom);
  const clearSelections = useSetAtom(clearSelectionsAtom);

  // Get test content to derive actual selected steps and operations
  const { data: testContent } = api.testContent.get.useQuery(
    { id: testContentId },
    { enabled: !!testContentId, refetchOnMount: false, refetchOnWindowFocus: false },
  );

  const selectedSteps = useMemo(() => {
    const selectedStepsIdsSet = new Set(selectedStepsIds);
    return (testContent?.steps ?? []).filter((step) => selectedStepsIdsSet.has(step.id));
  }, [selectedStepsIds, testContent?.steps]);

  const selectedOps = useMemo(() => {
    const selectedOpsIdsSet = new Set(selectedOpsIds);
    return testContent?.steps.flatMap((step) => step.operations).filter((op) => selectedOpsIdsSet.has(op.id)) ?? [];
  }, [selectedOpsIds, testContent?.steps]);

  // Mutations
  const { mutateAsync: createComponent } = api.component.create.useMutation();
  const { mutateAsync: upsertOperation } = api.operation.upsertOperation.useMutation();

  const form = useForm({
    initialValues: {
      name: '',
    },
    validate: zod4Resolver(
      z.object({
        name: z.string().min(1, 'Component name is required'),
      }),
    ),
  });

  const handleSubmit = async (values: typeof form.values) => {
    if (isCreating) {
      return;
    }

    setIsCreating(true);
    try {
      // Collect all operation IDs from selected steps and operations
      const operationIds = [
        ...selectedSteps.flatMap((step) => step.operations.map((op) => op.id)),
        ...selectedOps.map((op) => op.id),
      ];

      if (operationIds.length === 0) {
        notifications.show({
          title: 'Error',
          message: 'No operations selected for component creation',
          color: 'red',
        });
        return;
      }

      // Create the component with the operation IDs
      const component = await createComponent({
        projectId: projectId!,
        name: values.name,
        parameters: [], // Start with no parameters - can be added later
        outputVariables: [], // Start with no output variables - can be added later
        operationIds,
      });

      // Get the step ID where we should place the component call
      const targetStepId = selectedOps[0]?.testStep?.id || selectedSteps[0]?.id;

      if (!targetStepId) {
        throw new Error('No target step found for component call');
      }

      // Create a component call operation to replace the selected operations
      const componentCallOperation = {
        operationId: createId(),
        order: Math.min(
          ...(selectedOps.length > 0 ?
            selectedOps.map((op) => op.order)
          : selectedSteps.flatMap((step) => step.operations.map((op) => op.order))),
        ),
        stepId: targetStepId,
        pageAlias: selectedOps[0]?.pageAlias || selectedSteps[0]?.operations[0]?.pageAlias || 'page',
        operation: {
          operationType: 'COMPONENT_CALL' as const,
          componentId: component.id,
          inputParameters: {},
        },
      };

      // Add the component call operation
      await upsertOperation({
        operation: componentCallOperation,
      });

      // Clear selections and show success
      clearSelections();

      notifications.show({
        title: 'Component created',
        message: `Component "${values.name}" was created and is now being used in your test`,
        color: 'green',
      });

      // Refresh the test content
      await trpcUtils.testContent.get.invalidate({ id: testContentId });

      // Close the modal
      modals.closeAll();
    } catch (error) {
      console.error('Error creating component:', error);
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to create component',
        color: 'red',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const itemTypeText =
    selectedItemType === 'operations' ? 'actions'
    : selectedItemType === 'steps' ? 'steps'
    : 'actions and steps';

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <div className="flex flex-col gap-4">
        <div>
          <Text
            size="sm"
            mb="md"
          >
            You&apos;re about to create a new component from {selectedItemsCount} selected {itemTypeText}.
          </Text>

          <Alert
            icon={<IconInfoCircle size={16} />}
            color="blue"
            variant="light"
          >
            <Text size="sm">
              Creating this component will immediately replace the selected {itemTypeText} in your current test with a
              component call.
            </Text>
          </Alert>
        </div>

        <TextInput
          label="Component Name"
          placeholder="e.g. Login"
          autoFocus
          data-autofocus
          required
          {...form.getInputProps('name')}
        />

        <Group
          justify="flex-end"
          gap="xs"
        >
          <Button
            variant="light"
            onClick={onCancel}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            loading={isCreating}
          >
            Create Component
          </Button>
        </Group>
      </div>
    </form>
  );
}
